/**
 * Data-Sync Run History (#132) — staff cross-org monitoring + recovery.
 *
 * One screen, every practice + clinic + data pool. Monitor the fleet at a
 * glance (KPIs + failures-first table with a duration-legible lifecycle bar),
 * then click a run to open the detail slide-out and fix it. v1 manual-refresh,
 * Revolution-first (the primary pain point).
 *
 * Design source of truth: variant-f-final mockup. Renders real run data via
 * `listDataSyncRuns`; fields the pipeline doesn't persist yet (machine name,
 * written-vs-scraped) show as "—" rather than faked.
 */

import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  listDataSyncRuns,
  setRunSuccessOverride,
  rerunDataSyncRun,
  isRerunnable,
  type DataSyncRun,
  type DataSyncRunsResult,
  type SyncPool,
  type SyncStatus,
  type SyncWindow,
} from '../services/dataSyncRuns'
import { scrubVendorText } from '../utils/scrubVendorText'
import { humanizeSyncError } from '../utils/humanizeSyncError'
import './DataSyncRunHistory.css'

// Lifecycle bars auto-scale to the largest run in view (see niceCeilMs / axisTicks).

const POOL_LABEL: Record<SyncPool, string> = {
  revolution: 'Revolution',
  vsp: 'VSP',
  eyemed: 'EyeMed',
  versant: 'Versant',
  csv: 'CSV',
}

const STATUS_LABEL: Record<SyncStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  errored: 'Errored',
}

type QuickFilter = 'failed' | 'completed' | 'running' | 'queued' | 'retried' | 'all'
type SortKey = 'smart' | 'practice' | 'clinic' | 'pool' | 'started' | 'attempt' | 'status' | 'duration'

// ── formatting helpers ──────────────────────────────────────────────────

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—'
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}

function fmtPerPatient(ms: number | null | undefined): string {
  if (ms == null) return '—'
  return `${(ms / 1000).toFixed(1)}s`
}

function fmtAgo(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms)) return '—'
  if (ms < 60_000) return 'now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

/** Normalize a stored targetDate (YYYY-MM-DD or MM/DD/YYYY) to YYYY-MM-DD. */
function isoDay(s: string | null): string | null {
  if (!s) return null
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function pct(ms: number | null, axisMaxMs: number): number {
  if (ms == null || ms <= 0 || axisMaxMs <= 0) return 0
  return Math.min(100, (ms / axisMaxMs) * 100)
}

/** Median of a number list, or null when empty. Mirrors the backend summary
 *  computation so the client-recomputed KPIs match what the server returns. */
function median(nums: number[]): number | null {
  if (nums.length === 0) return null
  const s = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2)
}

/** Age (ms) of the oldest still-queued run in the list, or null when none are
 *  queued. Module-scoped so the Date.now() read stays out of render — same
 *  pattern as liveRunMs / fmtAgo — keeping the KPI memo lint-pure. */
function oldestQueuedAgeMs(runs: DataSyncRun[]): number | null {
  const now = Date.now()
  const ages = runs
    .filter((r) => r.status === 'queued')
    .map((r) => now - Date.parse(r.initiatedAt))
    .filter((n) => Number.isFinite(n) && n >= 0)
  return ages.length ? Math.max(...ages) : null
}

/** Round an axis max up to a clean minute/hour step so tick labels read nicely. */
function niceCeilMs(ms: number): number {
  const min = ms / 60_000
  const steps = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440]
  for (const s of steps) if (min <= s) return s * 60_000
  return Math.ceil(min / 60) * 60 * 60_000
}

function fmtAxisTick(ms: number): string {
  const min = Math.round(ms / 60_000)
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60); const m = min % 60
  return m ? `${h}h ${m}m` : `${h}h`
}

/** Five evenly-spaced ticks (0 → axisMax) with formatted labels + % offsets. */
function axisTicks(axisMaxMs: number): { label: string; pct: number }[] {
  return [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    label: f === 0 ? '0' : fmtAxisTick(axisMaxMs * f),
    pct: f === 0 ? 0 : f === 1 ? 99 : f * 100,
  }))
}

/** Effective "how long it ran" for a row, accounting for in-flight runs. */
function liveRunMs(run: DataSyncRun): number | null {
  if (run.runDurationMs != null) return run.runDurationMs
  if (run.status === 'running' && run.startedAt) return Date.now() - Date.parse(run.startedAt)
  return null
}

/** Total wall-clock span of a run (queue wait + run time), in ms. */
function totalMs(run: DataSyncRun): number {
  return (run.queueWaitMs ?? 0) + (liveRunMs(run) ?? 0)
}

/** Text for the right-aligned Duration column (status conveyed by color, not a prefix). */
function durationText(run: DataSyncRun): string {
  const pt = run.patientCount != null ? ` · ${run.patientCount}pt` : ''
  if (run.status === 'queued') return `Queued ${fmtDuration(Date.now() - Date.parse(run.initiatedAt))}`
  if (run.status === 'running') return `${fmtDuration(liveRunMs(run))}…${pt}`
  return `${fmtDuration(liveRunMs(run))}${pt}`
}

function durationColor(run: DataSyncRun): string {
  return run.status === 'errored' ? ' dsh-durcol--bad' : run.status === 'running' ? ' dsh-durcol--warn' : ''
}

// ── lifecycle bar ───────────────────────────────────────────────────────

function LifecycleBar({ run, axisMaxMs }: { run: DataSyncRun; axisMaxMs: number }) {
  const queuePct = pct(run.queueWaitMs, axisMaxMs)
  const runRaw = pct(liveRunMs(run), axisMaxMs)
  const runPct = runRaw > 0 ? Math.max(runRaw, 1.5) : 0 // keep short runs visible next to long waits
  let left = 0
  const segs: { cls: string; left: number; width: number }[] = []
  if (queuePct > 0) { segs.push({ cls: 'q', left, width: queuePct }); left += queuePct }
  if (runPct > 0) {
    const runCls = run.status === 'completed' ? 'ok' : run.status === 'errored' ? 'err' : 'run'
    segs.push({ cls: `${runCls}${run.status === 'running' ? ' live' : ''}`, left, width: runPct })
    left += runPct
  } else if (run.status === 'errored') {
    segs.push({ cls: 'err', left, width: 2 }); left += 2
  }
  return (
    <div className="dsh-track">
      <div className="dsh-track__base" />
      {[25, 50, 75].map((g) => <div key={g} className="dsh-track__grid" style={{ left: `${g}%` }} />)}
      {segs.map((sg, i) => (
        <span key={i} className={`dsh-seg dsh-seg--${sg.cls}`} style={{ left: `${sg.left}%`, width: `${sg.width}%` }} />
      ))}
    </div>
  )
}

// ── detail slide-out ────────────────────────────────────────────────────

/**
 * What re-running each pool actually does, said plainly under the button —
 * the two paths differ and the operator should know which one they're firing.
 */
const RERUN_HINT: Record<SyncPool, string> = {
  revolution: 'Re-pulls this clinic-day from Revolution — rewrites the day, including cancellations',
  vsp: 'Runs a fresh VSP check and retires this one — asks first if a recent check can be reused',
  eyemed: 'Runs a fresh EyeMed check and retires this one — asks first if a recent check can be reused',
  versant: 'Runs a fresh Versant check and retires this one — asks first if a recent check can be reused',
  csv: 'CSV uploads have no bot to re-fire',
}

function DetailDrawer(
  { run, onClose, onOverride, onRerun, rerunning, dialogOpen }: {
    run: DataSyncRun
    onClose: () => void
    onOverride: (override: boolean, reason?: string) => Promise<void>
    onRerun: () => void
    rerunning: boolean
    /** A confirm dialog is stacked on top and owns Escape + the backdrop. */
    dialogOpen: boolean
  },
) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const rerunnable = isRerunnable(run.pool)

  // Escape closes the drawer — unless a confirm dialog is stacked above it, in
  // which case the dialog owns the key. Without this the drawer closes UNDER an
  // open dialog, and confirming then hits `handleRerun`'s `!selected` guard and
  // silently does nothing.
  useEffect(() => {
    if (dialogOpen) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, dialogOpen])

  async function submit(override: boolean) {
    setBusy(true)
    try { await onOverride(override, override ? reason : undefined) }
    finally { setBusy(false) }
  }

  const drawerAxisMs = Math.max(totalMs(run), 60_000)
  const queuePct = pct(run.queueWaitMs, drawerAxisMs)
  const runPct = pct(liveRunMs(run), drawerAxisMs)
  const runCls = run.status === 'completed' ? 'ok' : run.status === 'errored' ? 'err' : 'run'

  return (
    <>
      <div className="dsh-overlay" onClick={onClose} />
      <aside className="dsh-drawer" role="dialog" aria-label="Run detail">
        <div className="dsh-drawer__top">
          <div>
            <div className="dsh-drawer__practice">{run.practiceName}</div>
            <h2 className="dsh-drawer__clinic">{run.clinicName}</h2>
            <div className="dsh-drawer__pool">{POOL_LABEL[run.pool]} · {run.gcpExecution ?? run.uipathJobId ?? 'not dispatched'} · {fmtAgo(run.initiatedAt)}</div>
            <div className="dsh-badges">
              <span className={`dsh-bigstat dsh-bigstat--${run.status}`}>{STATUS_LABEL[run.status].toUpperCase()}</span>
              <span className="dsh-trig">attempt {run.attempt}</span>
              {run.overridden && <span className="dsh-trig dsh-trig--ovr">staff-corrected</span>}
            </div>
          </div>
          <button className="dsh-drawer__x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="dsh-dh">Where the time went</div>
        <div className="dsh-axisbar">
          <div className="dsh-ruler">
            {axisTicks(drawerAxisMs).map((t) => (
              <span key={`${t.label}-${t.pct}`} className="dsh-tk" style={{ left: `${t.pct}%` }}>{t.label}</span>
            ))}
          </div>
          <div className="dsh-ptrack">
            <div className="dsh-track__base" />
            {queuePct > 0 && <span className="dsh-seg dsh-seg--q" style={{ left: '0%', width: `${queuePct}%` }} />}
            {runPct > 0 && <span className={`dsh-seg dsh-seg--${runCls}`} style={{ left: `${queuePct}%`, width: `${Math.max(runPct, 1.5)}%` }} />}
          </div>
          <div className="dsh-flagcap">
            {run.queueWaitMs ? `waited ${fmtDuration(run.queueWaitMs)} · ` : ''}ran {fmtDuration(liveRunMs(run))}{run.timePerPatientMs != null ? ` · ${fmtPerPatient(run.timePerPatientMs)} / patient` : ''}
          </div>
        </div>

        <div className="dsh-dh">How long each stage took</div>
        <table className="dsh-stages">
          <tbody>
            <tr><td className="dsh-stage"><span className="dsh-dot" style={{ background: '#cbd5e1' }} />Initiated<span className="dsh-kind">started</span></td><td className="dsh-dur">{new Date(run.initiatedAt).toLocaleTimeString()}</td></tr>
            <tr><td className="dsh-stage"><span className="dsh-dot" style={{ background: '#64748b' }} />Queued<span className="dsh-kind">waited</span></td><td className="dsh-dur">{fmtDuration(run.queueWaitMs)}</td></tr>
            <tr><td className="dsh-stage"><span className="dsh-dot" style={{ background: '#f59e0b' }} />Running<span className="dsh-kind">ran for</span></td><td className="dsh-dur dsh-dur--big">{fmtDuration(liveRunMs(run))}</td></tr>
            <tr><td className="dsh-stage"><span className="dsh-dot" style={{ background: run.status === 'completed' ? '#10b981' : '#ef4444' }} />{run.status === 'completed' ? 'Completed' : run.status === 'errored' ? 'Errored' : 'Ended'}<span className="dsh-kind">ended</span></td><td className="dsh-dur">{run.endedAt ? new Date(run.endedAt).toLocaleTimeString() : '—'}</td></tr>
          </tbody>
        </table>

        <div className="dsh-dh">Volume &amp; throughput</div>
        <div className="dsh-kv">
          <div className="dsh-kv__row"><span className="dsh-k">Patients pulled</span><span className="dsh-v">{run.patientCount ?? '—'}</span></div>
          <div className="dsh-kv__row"><span className="dsh-k">Time / patient</span><span className="dsh-v">{fmtPerPatient(run.timePerPatientMs)}</span></div>
          <div className="dsh-kv__row"><span className="dsh-k">Target day</span><span className="dsh-v">{run.targetDate ?? '—'}</span></div>
        </div>

        {(run.errorHistory.length > 0 || run.error) && (
          <>
            <div className="dsh-dh">Attempt history · {run.attempt} attempt{run.attempt === 1 ? '' : 's'}</div>
            <div className="dsh-kv">
              {run.errorHistory.length > 0
                ? run.errorHistory.map((e, i) => (
                  <div key={i} className="dsh-kv__row">
                    <span className="dsh-k">Attempt {i + 1}{e.timestamp ? ` · ${new Date(e.timestamp).toLocaleTimeString()}` : ''}</span>
                    <span className="dsh-v dsh-v--bad">{scrubVendorText(humanizeSyncError(e.error))}</span>
                  </div>
                ))
                : <div className="dsh-kv__row"><span className="dsh-k">Latest</span><span className="dsh-v dsh-v--bad">{scrubVendorText(humanizeSyncError(run.error))}</span></div>}
            </div>
          </>
        )}

        {run.gcpExecution ? (
          <>
            <div className="dsh-dh">In-house scraper (Cloud Run)</div>
            <div className="dsh-kv">
              <div className="dsh-kv__row"><span className="dsh-k">Execution</span><span className="dsh-v">{run.gcpExecution}</span></div>
              <div className="dsh-kv__row"><span className="dsh-k">Logs</span>
                {run.gcpLogsUrl
                  ? <a className="dsh-v dsh-link" href={run.gcpLogsUrl} target="_blank" rel="noreferrer">View run logs in Cloud Logging ↗</a>
                  : <span className="dsh-v dsh-v--muted">no logs link for this run</span>}
              </div>
              <div className="dsh-kv__row"><span className="dsh-k">Artifacts</span>
                {run.gcpArtifactsUrl
                  ? <a className="dsh-v dsh-link" href={run.gcpArtifactsUrl} target="_blank" rel="noreferrer">View trace and screenshots in Cloud Storage ↗</a>
                  : <span className="dsh-v dsh-v--muted">no artifacts link for this run</span>}
              </div>
            </div>
          </>
        ) : run.uipathJobId ? (
          // A run the retired engine performed: what it stored, as text — there
          // is nothing left to link to.
          <>
            <div className="dsh-dh">Engine: bot (retired)</div>
            <div className="dsh-kv">
              <div className="dsh-kv__row"><span className="dsh-k">Job Id</span><span className="dsh-v">{run.uipathJobId}</span></div>
              <div className="dsh-kv__row"><span className="dsh-k">Machine</span><span className="dsh-v">{run.uipathMachine ?? '—'}</span></div>
            </div>
          </>
        ) : (
          // A lane row with no execution: queued and waiting for the drain, or
          // a row (a demo fixture, a very old record) that recorded none.
          <>
            <div className="dsh-dh">In-house scraper (Cloud Run)</div>
            <div className="dsh-kv">
              <div className="dsh-kv__row"><span className="dsh-k">Execution</span>
                <span className="dsh-v dsh-v--muted">
                  {run.status === 'queued' || run.status === 'running' ? 'Not dispatched yet' : 'not recorded for this run'}
                </span>
              </div>
            </div>
          </>
        )}

        <div className="dsh-dh">Input parameters sent to the scraper</div>
        <div className="dsh-kv">
          {run.uipathJobInputs
            ? Object.entries(run.uipathJobInputs).map(([k, v]) => (
              <div key={k} className="dsh-kv__row"><span className="dsh-k dsh-k--mono">{k}</span><span className="dsh-v">{v || '—'}</span></div>
            ))
            : <div className="dsh-kv__row"><span className="dsh-k">Inputs</span><span className="dsh-v dsh-v--muted">not recorded for this run</span></div>}
        </div>

        {run.overridden && run.successOverride ? (
          <div className="dsh-ovr-box dsh-ovr-box--on">
            <div className="dsh-ovr-box__title">✓ Marked as actually succeeded</div>
            <div className="dsh-ovr-box__meta">
              by {run.successOverride.actorEmail}
              {run.successOverride.createdAt ? ` · ${fmtAgo(run.successOverride.createdAt)}` : ''}
              {' · counts as succeeded in staff stats only — the customer view still shows the error.'}
            </div>
            <div className="dsh-ovr-box__reason">&ldquo;{run.successOverride.reason}&rdquo;</div>
            <button className="dsh-btn-undo" disabled={busy} onClick={() => submit(false)}>
              {busy ? 'Removing…' : 'Undo — back to errored'}
            </button>
          </div>
        ) : run.status === 'errored' ? (
          <div className="dsh-ovr-box">
            <div className="dsh-ovr-box__title">Mark as actually succeeded</div>
            <div className="dsh-ovr-box__meta">
              Staff-only correction. Only the fleet stats here change; the customer view is unchanged.
            </div>
            <textarea
              className="dsh-ovr-reason"
              placeholder="Reason (required) — why was this actually a success?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
            <button
              className="dsh-btn-mark"
              disabled={busy || reason.trim().length < 3}
              onClick={() => submit(true)}
            >
              {busy ? 'Saving…' : '✓ Mark as succeeded'}
            </button>
          </div>
        ) : null}

        <div className="dsh-retry-bar">
          <button
            className="dsh-btn-retry"
            disabled={!rerunnable || rerunning}
            title={rerunnable ? undefined : 'CSV uploads have no bot to re-fire'}
            onClick={() => onRerun()}
          >
            {rerunning ? 'Re-running…' : '↻ Retry run'}
          </button>
          <div className="dsh-retry-sub">{RERUN_HINT[run.pool]}</div>
        </div>
      </aside>
    </>
  )
}

// ── main component ──────────────────────────────────────────────────────

export default function DataSyncRunHistory(
  { initialOrgId, initialClinicId, initialDate, initialPool }: {
    initialOrgId?: string | null; initialClinicId?: string | null;
    initialDate?: string | null; initialPool?: string | null;
  } = {},
) {
  const [data, setData] = useState<DataSyncRunsResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [window_, setWindow] = useState<SyncWindow>('7d')
  // When opened from a Heat Map "Pull failed" cell, land pre-filtered to that
  // clinic + Failed so the operator sees the exact failed runs.
  const [quick, setQuick] = useState<QuickFilter>(initialClinicId ? 'failed' : 'all')
  const [poolFilter, setPoolFilter] = useState<SyncPool | 'all'>(
    initialPool === 'revolution' || initialPool === 'vsp' || initialPool === 'eyemed' ||
      initialPool === 'versant' || initialPool === 'csv'
      ? initialPool : 'all',
  )
  const [practiceFilter, setPracticeFilter] = useState(initialOrgId ?? 'all')
  const [clinicFilter, setClinicFilter] = useState(initialClinicId ?? 'all')
  // Set when arriving from a Heat Map "Failed" cell — pins one appointment day.
  const [dateFilter, setDateFilter] = useState<string | null>(initialDate ?? null)
  const [selected, setSelected] = useState<DataSyncRun | null>(null)
  const [rerunning, setRerunning] = useState(false)
  // The appointment date awaiting a past-day confirmation, or null.
  const [confirmPast, setConfirmPast] = useState<string | null>(null)
  // True while a vendor re-run waits on "the cache answered — force a real one?"
  const [confirmReuse, setConfirmReuse] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>('smart')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  function refresh(w: SyncWindow) {
    setLoading(true)
    setError(null)
    listDataSyncRuns(w)
      .then((res) => { setData(res); setLoading(false) })
      .catch((err) => {
        Sentry.captureException(err)
        setError(err instanceof Error ? err.message : 'Failed to load run history.')
        setLoading(false)
      })
  }

  // Fetch when the time window changes. State is synced from an external
  // system (the Cloud Function), which is exactly what effects are for.
  useEffect(() => { refresh(window_) }, [window_])

  const runs = useMemo(() => data?.runs ?? [], [data])

  const practices = useMemo(() => {
    const m = new Map<string, string>()
    runs.forEach((r) => m.set(r.orgId, r.practiceName))
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [runs])

  const clinics = useMemo(() => {
    const m = new Map<string, string>()
    runs.filter((r) => practiceFilter === 'all' || r.orgId === practiceFilter)
      .forEach((r) => m.set(r.clinicId, r.clinicName))
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [runs, practiceFilter])

  // Runs in scope of the pool / practice / clinic / day filters — but NOT the
  // quick status chip. This is the base for the KPI tiles AND the quick-chip
  // counts, so every counter tracks the pool/practice/clinic/day selection.
  // The quick chips are the status filter themselves, so they don't filter
  // their own counts (selecting "Failed" must not zero out the other chips).
  const scoped = useMemo(() => {
    return runs.filter((r) => {
      if (poolFilter !== 'all' && r.pool !== poolFilter) return false
      if (practiceFilter !== 'all' && r.orgId !== practiceFilter) return false
      if (clinicFilter !== 'all' && r.clinicId !== clinicFilter) return false
      if (dateFilter && isoDay(r.targetDate) !== dateFilter) return false
      return true
    })
  }, [runs, poolFilter, practiceFilter, clinicFilter, dateFilter])

  // Table rows: scoped runs narrowed by the active quick (status) chip.
  const filtered = useMemo(() => {
    return scoped.filter((r) => {
      if (quick === 'failed') return r.status === 'errored'
      if (quick === 'completed') return r.status === 'completed'
      if (quick === 'running') return r.status === 'running'
      if (quick === 'queued') return r.status === 'queued'
      if (quick === 'retried') return r.attempt > 1
      return true
    })
  }, [scoped, quick])

  // KPI summary, recomputed client-side from the SCOPED runs so the tiles track
  // the pool/practice/clinic/day filters. The backend computes its summary the
  // same way over the same (capped) run set, so with no filter active these
  // numbers are identical to data.summary — they only narrow when filtering.
  const summary = useMemo(() => {
    const failed = scoped.filter((r) => r.status === 'errored').length
    const running = scoped.filter((r) => r.status === 'running').length
    const queued = scoped.filter((r) => r.status === 'queued').length
    const completed = scoped.filter((r) => r.status === 'completed').length
    const terminal = completed + failed
    const tpp: Partial<Record<SyncPool, number>> = {}
    ;(['revolution', 'vsp', 'eyemed', 'versant', 'csv'] as SyncPool[]).forEach((pool) => {
      const vals = scoped
        .filter((r) => r.pool === pool && r.status === 'completed' && r.timePerPatientMs != null)
        .map((r) => r.timePerPatientMs as number)
      const m = median(vals)
      if (m != null) tpp[pool] = m
    })
    return {
      totalRuns: scoped.length,
      failed, running, queued, completed,
      successRatePct: terminal > 0 ? Math.round((completed / terminal) * 1000) / 10 : null,
      timePerPatientMs: tpp,
      oldestQueuedMs: oldestQueuedAgeMs(scoped),
      practiceCount: new Set(scoped.map((r) => r.orgId)).size,
      clinicCount: new Set(scoped.map((r) => `${r.orgId}/${r.clinicId}`)).size,
    }
  }, [scoped])

  // Default sort ('smart'): failures bubble to the top, then most-recent first.
  // Clicking a column header overrides it.
  const sorted = useMemo(() => {
    const arr = [...filtered]
    if (sortKey === 'smart') {
      const rank: Record<SyncStatus, number> = { errored: 0, running: 1, queued: 2, completed: 3 }
      return arr.sort((a, b) =>
        rank[a.status] - rank[b.status] || Date.parse(b.initiatedAt) - Date.parse(a.initiatedAt))
    }
    const dir = sortDir === 'asc' ? 1 : -1
    const val = (r: DataSyncRun): string | number => {
      switch (sortKey) {
        case 'practice': return r.practiceName.toLowerCase()
        case 'clinic': return r.clinicName.toLowerCase()
        case 'pool': return r.pool
        case 'started': return Date.parse(r.initiatedAt)
        case 'attempt': return r.attempt
        case 'status': return r.status
        case 'duration': return liveRunMs(r) ?? -1
        default: return 0
      }
    }
    return arr.sort((a, b) => {
      const av = val(a); const bv = val(b)
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [filtered, sortKey, sortDir])

  // Auto-scale the lifecycle axis to the longest run currently in view.
  const axisMaxMs = useMemo(
    () => niceCeilMs(Math.max(5 * 60_000, ...sorted.map(totalMs))),
    [sorted],
  )

  function toggleSort(k: SortKey) {
    if (k === sortKey) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return }
    setSortKey(k)
    setSortDir(k === 'started' || k === 'duration' || k === 'attempt' ? 'desc' : 'asc')
  }
  const sortArrow = (k: SortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '')

  // Staff success-override: mark the selected errored run as succeeded (or undo),
  // then refresh so the table + KPIs reflect it and close the drawer.
  async function handleOverride(override: boolean, reason?: string) {
    if (!selected) return
    try {
      await setRunSuccessOverride(selected, override, reason)
      toast.success(override ? 'Marked as succeeded.' : 'Override removed — back to errored.')
      setSelected(null)
      refresh(window_)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? scrubVendorText(err.message) : 'Could not update the run.')
    }
  }

  // Staff rerun: re-fire the bot behind the selected run. A past Revolution day
  // comes back as `confirm_past` instead of executing — a forced re-pull
  // rewrites that day (cancellation pass included), so it takes a second click.
  // The drawer stays OPEN on success: the operator is usually watching the run
  // they just re-fired, and the refreshed list re-renders the row underneath.
  async function handleRerun(opts: { confirmPast?: boolean; force?: boolean } = {}) {
    if (!selected || !isRerunnable(selected.pool)) return
    const target = { orgId: selected.orgId, id: selected.id, pool: selected.pool }
    setRerunning(true)
    try {
      const res = await rerunDataSyncRun(target, opts)
      if (res.needsConfirm === 'confirm_past') {
        setConfirmPast(res.appointmentDate ?? 'that day')
        return
      }
      // A recent check already answers this, so nothing was dispatched. Offer
      // the bypass rather than reporting a re-run that didn't happen.
      if (res.needsConfirm === 'reused') {
        setConfirmReuse(true)
        return
      }
      // Report the outcome, not the round-trip. `executePull` turns a dispatch
      // failure into a `failed` RESULT rather than throwing (the lane's
      // execution start failed), and an empty `results` means the transactional
      // claim lost a race to the hourly pull — both would read as "started"
      // under a plain skipped/else split, which is the worst possible lie for a
      // tool operators reach for mid-incident. `reason` is shown raw: this is
      // the internal console, so "dispatch_failed" IS the answer.
      const failed = res.results.find((r) => r.action === 'failed')
      const skipped = res.results.find((r) => r.action === 'skipped')
      if (failed) toast.error(failed.reason ?? 'Dispatch failed.')
      else if (skipped) toast.warning(skipped.reason ?? 'Nothing to re-run.')
      else if (res.results.length === 0) {
        toast.warning('Nothing dispatched — the run was claimed by another pull.')
      } else if (res.results.some((r) => r.action === 'reused')) {
        // The check cache answered with an existing check — correct behaviour
        // (a recent same-DOS answer is reusable), but no bot ran and the source
        // was not retired, so it must not read as a fresh check.
        toast.warning('Existing check reused — no new bot run.')
      } else toast.success(res.pool === 'revolution' ? 'Re-pull started.' : 'New check started.')
      refresh(window_)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? scrubVendorText(err.message) : 'Could not re-run.')
    } finally {
      setRerunning(false)
    }
  }

  // Show the scoped summary once data has loaded; null keeps the "—" placeholder
  // tiles before the first fetch resolves.
  const s = data ? summary : null
  const tpp = s?.timePerPatientMs ?? {}

  return (
    <div className="dsh">
      <div className="dsh-head">
        <div>
          <h1>Data-Sync Run History</h1>
          <div className="dsh-subt">Internal ops · all practices · all clinics · all pulls</div>
        </div>
        <div className="dsh-head-meta">
          {data && <>Window: <b>{window_}</b> · {data.practiceCount} practices · {data.clinicCount} clinics</>}
          <button className="dsh-refresh" onClick={() => refresh(window_)} disabled={loading}>↻ {loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="dsh-kpis">
        <div className="dsh-kpi"><div className="dsh-kpi__l">Total runs</div><div className="dsh-kpi__n">{s?.totalRuns ?? '—'}</div><div className="dsh-kpi__d">{s ? `${s.practiceCount} practices · ${s.clinicCount} clinics` : ''}</div></div>
        <div className={`dsh-kpi${(s?.failed ?? 0) > 0 ? ' dsh-kpi--alert' : ''}`}><div className="dsh-kpi__l">Failed</div><div className="dsh-kpi__n dsh-kpi__n--bad">{s?.failed ?? '—'}</div><div className="dsh-kpi__d">in this window</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">Success rate</div><div className="dsh-kpi__n dsh-kpi__n--good">{s?.successRatePct != null ? `${s.successRatePct}%` : '—'}</div><div className="dsh-kpi__d">{s ? `${s.completed} / ${s.completed + s.failed} ok` : ''}</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">Time / patient</div><div className="dsh-kpi__n">{fmtPerPatient(tpp.revolution)}<span className="dsh-kpi__pn">Revolution</span></div><div className="dsh-kpi__d">VSP {fmtPerPatient(tpp.vsp)} · EyeMed {fmtPerPatient(tpp.eyemed)}</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">Queue</div><div className="dsh-kpi__n">{s?.queued ?? '—'}<span className="dsh-kpi__pn">queued · {s?.running ?? 0} running</span></div><div className="dsh-kpi__d">{s?.oldestQueuedMs != null ? `oldest queued ${fmtDuration(s.oldestQueuedMs)}` : 'nothing queued'}</div></div>
      </div>

      {/* quick filters + dropdowns */}
      <div className="dsh-qf">
        {dateFilter && (
          <button className="dsh-chip dsh-chip--on dsh-chip--day" onClick={() => setDateFilter(null)} title="Clear the day filter">
            Day {dateFilter.slice(5)} ✕
          </button>
        )}
        {([['failed', 'Failed'], ['completed', 'Completed'], ['running', 'Running'], ['queued', 'Queued'], ['retried', 'Retried'], ['all', 'All runs']] as [QuickFilter, string][]).map(([k, label]) => (
          <button key={k} className={`dsh-chip${quick === k ? ' dsh-chip--on' : ''}${k === 'failed' ? ' dsh-chip--alert' : ''}`} onClick={() => setQuick(k)}>
            {label} <span className="dsh-c">{k === 'all' ? scoped.length : k === 'retried' ? scoped.filter((r) => r.attempt > 1).length : scoped.filter((r) => r.status === (k === 'failed' ? 'errored' : k)).length}</span>
          </button>
        ))}
        <div className="dsh-qf__right">
          <select className="dsh-ddl" value={poolFilter} onChange={(e) => setPoolFilter(e.target.value as SyncPool | 'all')}>
            <option value="all">All pulls</option>
            <option value="revolution">Revolution</option>
            <option value="vsp">VSP</option>
            <option value="eyemed">EyeMed</option>
            <option value="versant">Versant</option>
            <option value="csv">CSV</option>
          </select>
          <select className="dsh-ddl" value={practiceFilter} onChange={(e) => { setPracticeFilter(e.target.value); setClinicFilter('all') }}>
            <option value="all">All practices</option>
            {practices.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className="dsh-ddl" value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value)}>
            <option value="all">All clinics</option>
            {clinics.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className="dsh-ddl" value={window_} onChange={(e) => setWindow(e.target.value as SyncWindow)}>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
          </select>
        </div>
      </div>

      {error && <p className="dsh-error">{error}</p>}
      {loading && !data && <p className="dsh-loading">Loading run history…</p>}

      {data && (
        <div className="dsh-card">
          <table className="dsh-table">
            <thead>
              <tr>
                <th className="dsh-sortable" onClick={() => toggleSort('practice')}>Practice{sortArrow('practice')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('clinic')}>Clinic{sortArrow('clinic')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('pool')}>Pull{sortArrow('pool')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('started')}>Started{sortArrow('started')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('attempt')}>Att{sortArrow('attempt')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
                <th className="dsh-life-h">Lifecycle
                  <div className="dsh-lhead">
                    {axisTicks(axisMaxMs).map((t) => (
                      <span key={`${t.label}-${t.pct}`} className="dsh-tk" style={{ left: `${t.pct}%` }}>{t.label}</span>
                    ))}
                  </div>
                </th>
                <th className="dsh-dur-h dsh-sortable" onClick={() => toggleSort('duration')}>Duration{sortArrow('duration')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan={8} className="dsh-empty">No runs match these filters.</td></tr>}
              {sorted.map((run) => (
                <tr key={`${run.orgId}/${run.id}`} className={`${run.status === 'errored' ? 'dsh-err-row' : ''}${selected?.id === run.id && selected?.orgId === run.orgId ? ' dsh-sel' : ''}`} onClick={() => setSelected(run)}>
                  <td className="dsh-practice">{run.practiceName}</td>
                  <td className="dsh-clinic">{run.clinicName}</td>
                  <td><span className={`dsh-pool${run.pool === 'revolution' ? ' dsh-pool--rev' : ''}`}>{POOL_LABEL[run.pool]}</span></td>
                  <td className="dsh-ts">{fmtAgo(run.initiatedAt)}</td>
                  <td>{run.attempt > 1 ? <span className="dsh-att dsh-att--retry">{run.attempt}</span> : <span className="dsh-att">1</span>}</td>
                  <td>
                    <span className={`dsh-status dsh-status--${run.status}`}><span className="dsh-d" />{STATUS_LABEL[run.status]}</span>
                    {run.overridden && (
                      <span
                        className="dsh-ovr-badge"
                        title={run.successOverride ? `Marked succeeded by ${run.successOverride.actorEmail}` : 'Staff-corrected'}
                      >corrected</span>
                    )}
                    {run.status === 'errored' && run.error && <div className="dsh-snip">{scrubVendorText(humanizeSyncError(run.error))}</div>}
                  </td>
                  <td><LifecycleBar run={run} axisMaxMs={axisMaxMs} /></td>
                  <td className={`dsh-durcol${durationColor(run)}`}>{durationText(run)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.capped && <p className="dsh-capped">
            {(data.cappedSources ?? []).map((p) => POOL_LABEL[p]).join(', ') || 'Some sources'} hit the row cap —
            the oldest runs in this window (including any failures among them) are not shown. Narrow the window to see them.
          </p>}
        </div>
      )}

      {selected && (
        <DetailDrawer
          run={selected}
          onClose={() => setSelected(null)}
          onOverride={handleOverride}
          onRerun={handleRerun}
          rerunning={rerunning}
          dialogOpen={confirmPast !== null || confirmReuse}
        />
      )}

      {confirmPast && (
        <ConfirmPastDialog
          appointmentDate={confirmPast}
          busy={rerunning}
          onCancel={() => setConfirmPast(null)}
          onConfirm={() => { setConfirmPast(null); handleRerun({ confirmPast: true }) }}
        />
      )}

      {confirmReuse && selected && (
        <ConfirmReuseDialog
          pool={selected.pool}
          busy={rerunning}
          onCancel={() => setConfirmReuse(false)}
          onConfirm={() => { setConfirmReuse(false); handleRerun({ force: true }) }}
        />
      )}
    </div>
  )
}

/**
 * Escape dismisses the confirm dialog. The drawer underneath suppresses its own
 * Escape handler while a dialog is open (`dialogOpen`), so exactly one thing
 * responds to the key. Ignored mid-request: cancelling a dialog whose call is
 * already in flight would drop the operator's only view of the outcome.
 */
function useDialogEscape(onCancel: () => void, busy: boolean) {
  useEffect(() => {
    if (busy) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, busy])
}

/**
 * Past-day re-pull confirmation. A forced re-pull re-runs the cancellation pass
 * over a historical day, so the backend refuses the first call and hands back
 * `confirm_past`; this is the round-trip that satisfies it.
 */
function ConfirmPastDialog(
  { appointmentDate, busy, onCancel, onConfirm }: {
    appointmentDate: string
    busy: boolean
    onCancel: () => void
    onConfirm: () => void
  },
) {
  useDialogEscape(onCancel, busy)
  return (
    <>
      <div className="dsh-confirm-overlay" onClick={busy ? undefined : onCancel} />
      <div className="dsh-confirm" role="dialog" aria-modal="true" aria-label="Confirm past-day re-pull">
        <h3 className="dsh-confirm__title">Re-pull a past day?</h3>
        <p className="dsh-confirm__body">
          {appointmentDate} has already happened. Re-pulling rewrites that day from
          Revolution and re-runs the cancellation pass, so appointments that are no
          longer on the schedule get marked cancelled.
        </p>
        <div className="dsh-confirm__actions">
          <button className="dsh-btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="dsh-btn-retry" onClick={onConfirm} disabled={busy}>
            {busy ? 'Re-running…' : 'Re-pull anyway'}
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Cache-bypass confirmation for a vendor re-run. The first call found a recent
 * check with the same patient and date-of-service, so the dedup cache answered
 * with it and NO bot ran — correct, but not what an operator clicking "Retry
 * run" is asking for. Confirming re-sends with `force`, which skips the reuse
 * lookup and buys a real portal scrape.
 */
function ConfirmReuseDialog(
  { pool, busy, onCancel, onConfirm }: {
    pool: SyncPool
    busy: boolean
    onCancel: () => void
    onConfirm: () => void
  },
) {
  useDialogEscape(onCancel, busy)
  return (
    <>
      <div className="dsh-confirm-overlay" onClick={busy ? undefined : onCancel} />
      <div className="dsh-confirm" role="dialog" aria-modal="true" aria-label="Confirm forced re-check">
        <h3 className="dsh-confirm__title">Force a fresh {POOL_LABEL[pool]} check?</h3>
        <p className="dsh-confirm__body">
          A recent check already covers this patient and date of service, so nothing
          was re-run. Forcing one bypasses that and scrapes the portal again — it
          spends a bot run and creates a new check that supersedes the current answer.
        </p>
        <div className="dsh-confirm__actions">
          <button className="dsh-btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="dsh-btn-retry" onClick={onConfirm} disabled={busy}>
            {busy ? 'Re-running…' : 'Force re-check'}
          </button>
        </div>
      </div>
    </>
  )
}
